import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { Box, Button, Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { type Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';

// All boundary math is performed in UTC. User-local boundaries are out of
// scope on purpose — the period reports this picker drives use UTC date
// columns, so anchoring everything to UTC here means we never have to
// reconcile a half-open local-vs-UTC range on the wire.
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(quarterOfYear);

/**
 * Locked contract: forecastReports.dateRangePicker.v1.
 * Component name, Preset union, and props must not drift — A11 (Wave 3)
 * and F9 (Wave 4) both consume this directly.
 */
export type Preset =
  | 'this_month'
  | 'last_month'
  | 'this_quarter'
  | 'last_quarter'
  | 'ytd'
  | 'last_ytd'
  | 'custom';

export interface DateRangePresetPickerProps {
  readonly value: { from: string | null; to: string | null };
  readonly onChange: (range: { from: string; to: string; preset: Preset }) => void;
  readonly defaultPreset?: Preset;
}

const ISO_DATE = 'YYYY-MM-DD';

const PRESETS: ReadonlyArray<{ value: Preset; label: string }> = [
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: 'this_quarter', label: 'This quarter' },
  { value: 'last_quarter', label: 'Last quarter' },
  { value: 'ytd', label: 'YTD' },
  { value: 'last_ytd', label: 'Last YTD' },
  { value: 'custom', label: 'Custom' },
];

/**
 * Compute the {from, to} ISO date pair for a named preset, anchored at UTC.
 * Exported so consuming code and tests can verify date math without
 * mounting the component.
 */
// react-refresh complains because this file exports a non-component
// alongside the component. Co-locating the math with the only consumer
// outweighs HMR convenience — same trade-off as
// `feedback/errorBoundaryFallback.tsx`.
// eslint-disable-next-line react-refresh/only-export-components
export function computePresetRange(preset: Exclude<Preset, 'custom'>): {
  from: string;
  to: string;
} {
  const now = dayjs.utc();
  switch (preset) {
    case 'this_month':
      return {
        from: now.startOf('month').format(ISO_DATE),
        to: now.endOf('month').format(ISO_DATE),
      };
    case 'last_month': {
      const prev = now.subtract(1, 'month');
      return {
        from: prev.startOf('month').format(ISO_DATE),
        to: prev.endOf('month').format(ISO_DATE),
      };
    }
    case 'this_quarter':
      return {
        from: now.startOf('quarter').format(ISO_DATE),
        to: now.endOf('quarter').format(ISO_DATE),
      };
    case 'last_quarter': {
      const prev = now.subtract(1, 'quarter');
      return {
        from: prev.startOf('quarter').format(ISO_DATE),
        to: prev.endOf('quarter').format(ISO_DATE),
      };
    }
    case 'ytd': {
      const year = now.year();
      return {
        from: dayjs.utc(`${year}-01-01`).format(ISO_DATE),
        to: now.format(ISO_DATE),
      };
    }
    case 'last_ytd': {
      const lastYear = now.year() - 1;
      return {
        from: dayjs.utc(`${lastYear}-01-01`).format(ISO_DATE),
        to: dayjs.utc(`${lastYear}-12-31`).format(ISO_DATE),
      };
    }
  }
}

const toDayjs = (value: string | null): Dayjs | null => {
  if (!value) return null;
  const d = dayjs.utc(value);
  return d.isValid() ? d : null;
};

/**
 * Reusable date range selector for period-based reports.
 *
 * Renders an MUI ToggleButtonGroup with six named presets plus a Custom
 * option. Selecting any named preset immediately fires `onChange` with the
 * computed UTC range. Selecting Custom reveals two date pickers; the user
 * presses Apply to emit `{ from, to, preset: 'custom' }`.
 *
 * @see Contract `forecastReports.dateRangePicker.v1`.
 */
export const DateRangePresetPicker = ({
  value,
  onChange,
  defaultPreset,
}: DateRangePresetPickerProps): ReactElement => {
  // `null` until the user selects (or defaultPreset auto-selects) so the
  // ToggleButtonGroup has no initial highlight and re-clicking the same
  // preset emits a fresh range instead of being silently coalesced.
  const [preset, setPreset] = useState<Preset | null>(defaultPreset ?? null);
  const [customFrom, setCustomFrom] = useState<Dayjs | null>(() => toDayjs(value.from));
  const [customTo, setCustomTo] = useState<Dayjs | null>(() => toDayjs(value.to));

  // Emit the default preset range on mount so the parent's `value` is
  // populated. This mirrors how MUI ToggleButtonGroup users expect a
  // controlled component with a default selection to behave.
  useEffect(() => {
    if (defaultPreset && defaultPreset !== 'custom') {
      const range = computePresetRange(defaultPreset);
      onChange({ ...range, preset: defaultPreset });
    }
    // Intentionally only fires on mount — re-emitting on every onChange
    // identity change would cause an infinite loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePresetChange = (_event: React.MouseEvent<HTMLElement>, next: Preset | null): void => {
    if (!next) return;
    setPreset(next);
    if (next === 'custom') {
      // Wait for the user to press Apply — do not emit yet.
      return;
    }
    const range = computePresetRange(next);
    onChange({ ...range, preset: next });
  };

  const handleApplyCustom = (): void => {
    if (!customFrom || !customTo) return;
    onChange({
      from: customFrom.format(ISO_DATE),
      to: customTo.format(ISO_DATE),
      preset: 'custom',
    });
  };

  const customApplyDisabled = useMemo(
    () => !customFrom || !customTo || customFrom.isAfter(customTo),
    [customFrom, customTo],
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Stack spacing={1.5}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={preset}
          onChange={handlePresetChange}
          aria-label="Date range preset"
        >
          {PRESETS.map((opt) => (
            <ToggleButton
              key={opt.value}
              value={opt.value}
              aria-label={opt.label}
              data-preset={opt.value}
            >
              {opt.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {preset === 'custom' ? (
          <Stack direction="row" spacing={1} alignItems="center" data-testid="custom-range">
            <DatePicker
              label="From"
              value={customFrom}
              onChange={(next) => setCustomFrom(next)}
              format={ISO_DATE}
              slotProps={{ textField: { size: 'small', 'aria-label': 'From' } }}
            />
            <DatePicker
              label="To"
              value={customTo}
              onChange={(next) => setCustomTo(next)}
              format={ISO_DATE}
              slotProps={{ textField: { size: 'small', 'aria-label': 'To' } }}
            />
            <Box>
              <Button
                variant="contained"
                size="small"
                onClick={handleApplyCustom}
                disabled={customApplyDisabled}
              >
                Apply
              </Button>
            </Box>
          </Stack>
        ) : null}
      </Stack>
    </LocalizationProvider>
  );
};
